import { usePopup } from '@sparemin/blockhead';
import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { createCanvaDesign, exportCanvaDesign } from 'redux/modules/canva';
import { onClickCanvaButton } from 'redux/modules/mixpanel';
import { showError } from 'redux/modules/notification';
import { Dispatch } from 'redux/types';
import { AspectRatioName } from 'types';

export interface UseCanvaDesignConfig {
  source: Parameters<typeof onClickCanvaButton>[0];
  aspectRatioName: AspectRatioName;
  onClose?: () => void;
  onImageCreated?: (imageUrl: string, designId: string) => void;
}

export default function useCanvaDesign({
  source,
  aspectRatioName,
  onClose,
  onImageCreated,
}: UseCanvaDesignConfig) {
  const [currentDesignId, setCurrentDesignId] = useState<string | null>(null);
  const [currentDesignWindow, setCurrentDesignWindow] = useState<Window | null>(
    null,
  );

  const popupClosedManuallyRef = useRef(false);

  const dispatch = useDispatch<Dispatch>();

  const handleExportDesign = useCallback(async () => {
    if (popupClosedManuallyRef.current) {
      return;
    }

    try {
      const canvaExportResult = await dispatch(
        exportCanvaDesign(currentDesignId),
      );

      onImageCreated?.(canvaExportResult?.imageUrl, currentDesignId);
    } catch (error) {
      dispatch(showError('Error exporting canva design'));
    }
  }, [currentDesignId, dispatch, onImageCreated]);

  const { setPopupUrl, open, close } = usePopup({
    target: '_blank',
    onOpen: window => {
      popupClosedManuallyRef.current = false;
      setCurrentDesignWindow(window);
    },
    onClose: () => {
      onClose?.();
      handleExportDesign();
    },
  });

  const reopenPopup = useCallback(() => {
    currentDesignWindow?.focus();
  }, [currentDesignWindow]);

  const closePopup = useCallback(() => {
    popupClosedManuallyRef.current = true;
    close();
  }, [close]);

  const handleCreateDesign = useCallback(async () => {
    try {
      if (source) {
        dispatch(onClickCanvaButton(source));
      }

      const newDesign = await dispatch(createCanvaDesign(aspectRatioName));

      open();
      setPopupUrl(newDesign.editUrl);
      setCurrentDesignId(newDesign?.id);
    } catch (error) {
      dispatch(showError('Error creating canva design'));
    }
  }, [aspectRatioName, dispatch, open, setPopupUrl, source]);

  return {
    reopenPopup,
    closePopup,
    createDesign: handleCreateDesign,
  };
}
